import {
  Box,
  Container,
  Icon,
  Stack,
  Typography,
  useMediaQuery,
} from '@ace-clublabs/shared-component-library';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SearchResult } from '@/components/shared/SearchBox';
import type { Destination } from '@/features/destinations/utils/Destination';
import { destinationRegionLabelMapper } from '@/features/destinations/utils/DestinationRegionLabelMapper';
import type { ApiRegion } from '@/features/destinations/utils/Region';
import { useDebounce } from '@/hooks/useDebounce';
import useLandingPageSetup from '@/hooks/useLandingPageSetup';
import { Page } from '@/lib/tagging';
import useTagging from '@/lib/tagging/useTagging';
import useAppointmentStore from '@/store/AppointmentStore';
import useSessionStore from '@/store/SessionStore';
import { FlowType } from '@/types/FlowType';
import { formatSearchedDestination } from '@/utils/formatSearchedDestination';
import { useRelevanceSearch } from '../api/getRelevanceSearch';
import RelevanceAccordions from '../components/RelevanceAccordions';
import RSSearchBox from '../components/RSSearchBox';
import RSSearchBoxMobile from '../components/RSSearchBoxMobile';
import type { RSSearchBoxProps } from '../components/types';
import type { RelevanceSearchResult } from '../types/RelevanceSearchResult';

interface RelevanceSearchProps {
  flow: FlowType;
}

const undecidedKeyWords = [
  `don't know`,
  `dont know`,
  'not sure',
  'need help',
  `haven't picked`,
  'havent picked',
];

const RelevanceSearch: React.FC<RelevanceSearchProps> = ({ flow }) => {
  const [search, setSearch] = useState('');
  const [selectedResult, setSelectedResult] =
    useState<RelevanceSearchResult | null>(null);
  const [isWarningColor, setIsWarningColor] = useState(false);
  const [tagged, setTagged] = useState(false);

  const isEmailFlow = flow === FlowType.EMAIL;

  const setSelectedDestination = useAppointmentStore(
    (state) => state.setSelectedDestination,
  );

  const setSearchedDestination = useAppointmentStore(
    (state) => state.setSearchedDestination,
  );

  const setUserSearch = useAppointmentStore((state) => state.setUserSearch);

  const destinations = useAppointmentStore((state) => state.destinations);

  const searchedDestination = useAppointmentStore(
    (state) => state.searchedDestination,
  );

  const { userSearch } = useAppointmentStore();

  const { sessionId, sessionSource, flowType } = useSessionStore();
  const tagging = useTagging();

  const debouncedSearch = useDebounce(search, 300);
  const relevanceSearchQuery = useRelevanceSearch(debouncedSearch);

  const { handleClickContinue: onClickContinue } = useLandingPageSetup(flow);

  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const undecidedOption: SearchResult = useMemo(
    () => ({
      id: 'undecided',
      title: `I don't know, I haven't picked a destination yet.`,
      icon: 'circle-question-regular',
    }),
    [],
  );

  const searchResults: SearchResult[] | undefined = useMemo(() => {
    const lowerCasedSearch = debouncedSearch.toLowerCase();
    if (
      undecidedKeyWords.some((keyword) => lowerCasedSearch.includes(keyword))
    ) {
      return [undecidedOption];
    }

    if (!relevanceSearchQuery.data) return;
    if (!relevanceSearchQuery.data.length) {
      if (!debouncedSearch) return;
      return [];
    }

    const results = relevanceSearchQuery.data.map<SearchResult>((e) => {
      const isCountry = e.type?.includes('country');
      const isContinent = e.country === 'Unknown';

      return {
        id: e.id,
        title: e.name,
        icon: 'location-dot-duotone',
        subtitle:
          isCountry || isContinent
            ? undefined
            : [e.region, e.country].filter(Boolean).join(', '),
      };
    });
    return results;
  }, [relevanceSearchQuery.data, debouncedSearch, undecidedOption]);

  useEffect(() => {
    if (!tagged && sessionSource && sessionId) {
      const trvEndLocation = searchedDestination || userSearch || '';

      const commPref = isEmailFlow ? 'email' : undefined;

      tagging.tag('view', sessionSource || 'new', {
        page: Page.destination,
        sessionid: sessionId,
        flow: flowType || '',
        trv_end_location: trvEndLocation,
        ...(commPref && { comm_pref: commPref }),
      });

      setTagged(true);
    }
  }, [
    sessionId,
    flowType,
    sessionSource,
    tagging.tag,
    tagged,
    searchedDestination,
    userSearch,
    isEmailFlow,
  ]);

  const handleSearchChange = (search: string) => {
    setSearch(search);
  };

  const handleOtherDestination = useCallback(() => {
    const otherDestination = destinations.find((e) => e.name === 'Other');
    if (!otherDestination) return;
    setSelectedDestination(otherDestination);
    onClickContinue(otherDestination);
  }, [destinations, onClickContinue, setSelectedDestination]);

  const handleSelectResult = (result: SearchResult | null) => {
    if (!result) {
      setSelectedResult(null);
      return;
    }

    if (result.id === undecidedOption.id) {
      handleOtherDestination();
      return;
    }

    const selectedResult = relevanceSearchQuery.data?.find(
      (e) => e.id === result.id,
    );

    if (!selectedResult) return;
    setSelectedResult(selectedResult);
  };

  const handleUnmatchedDestination = useCallback(() => {
    if (!search) return;
    setUserSearch(search);
    setSearchedDestination('');
    handleOtherDestination();
  }, [search, handleOtherDestination, setUserSearch, setSearchedDestination]);

  const handleClickContinue = useCallback(() => {
    if (!selectedResult) {
      setIsWarningColor(true);
      handleUnmatchedDestination();
      return;
    }
    setIsWarningColor(false);
    const selectedDestination = destinations.find(
      (e) => e.name === selectedResult.aceDestination,
    );

    if (!selectedDestination) return;

    const mappedDestination: Destination = {
      ...selectedDestination,
      name: destinationRegionLabelMapper[selectedDestination.name as ApiRegion],
    };

    const searchedDestinationString = formatSearchedDestination([
      selectedResult.name,
      selectedResult.region,
      selectedResult.subRegion,
      selectedResult.country,
    ]);

    setSelectedDestination(mappedDestination);
    setSearchedDestination(searchedDestinationString);
    onClickContinue(selectedDestination);
  }, [
    selectedResult,
    onClickContinue,
    destinations,
    setSelectedDestination,
    setSearchedDestination,
    handleUnmatchedDestination,
  ]);

  const searchBoxProps: RSSearchBoxProps = {
    placeholder: isWarningColor
      ? 'Please type in a destination.'
      : 'Where would you like to travel?',
    isWarningColor: isWarningColor,
    onSelect: handleSelectResult,
    value: search,
    onChange: handleSearchChange,
    searchResults: searchResults,
    isLoading: relevanceSearchQuery.isLoading,
    onContinueClick: handleClickContinue,
  };

  return (
    <Stack
      direction='column'
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'auto',
        backgroundColor: '#F7F5F1',
      }}
      data-testid='relevance-search-page'
    >
      <Box
        sx={[
          {
            position: 'relative',
            width: '100%',
            minHeight: {
              xs: 'auto',
              md: '65vh',
            },
            backgroundColor: '#222222',
            py: 4,
          },
          isEmailFlow && {
            height: '100%',
          },
        ]}
        data-testid='relevance-search-hero-section'
      >
        <Box
          component='video'
          // @ts-expect-error
          src='/travel/agent/scheduler/relevanceSearchBgVideo.mp4'
          autoPlay
          loop
          muted
          playsInline
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
            borderRadius: '0 0 20px 20px',
          }}
          data-testid='relevance-search-hero-video'
        />

        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(10,10,10,0.4)',
            borderRadius: '0 0 20px 20px',
            zIndex: 1,
          }}
          aria-hidden
          data-testid='relevance-search-hero-overlay'
        />
        <Container
          maxWidth='lg'
          sx={{
            zIndex: 2,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          data-testid='relevance-search-hero-container'
        >
          <Stack
            direction='column'
            spacing={4}
            sx={{
              width: '100%',
              px: { md: 6, xs: 2 },
              pt: 6,
              pb: 5,
              borderRadius: 2,
              alignItems: 'center',
              zIndex: 3,
            }}
            data-testid='relevance-search-hero-stack'
          >
            <Box data-testid='relevance-search-hero-logo'>
              <Icon
                iconName='ace-orbit-logo'
                sx={{
                  color: '#fff',
                  width: 85,
                  height: 52,
                  '& path': {
                    fill: 'white',
                  },
                }}
              />
            </Box>
            <Typography
              variant='h3'
              sx={{ color: '#fff', textAlign: 'center' }}
              data-testid='relevance-search-hero-title'
            >
              {isEmailFlow
                ? 'Email a Travel Advisor'
                : 'Make an appointment with a travel advisor to plan and book travel'}
            </Typography>
            {isMobileView ? (
              <RSSearchBoxMobile {...searchBoxProps} />
            ) : (
              <RSSearchBox {...searchBoxProps} sx={{ width: '85%' }} />
            )}
          </Stack>
        </Container>
      </Box>
      {!isEmailFlow && <RelevanceAccordions />}
    </Stack>
  );
};

export default RelevanceSearch;
